// src/components/ApiSection.js

import React, { useState } from "react";
import {
  Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Button, Card, Paper, Collapse, Stack, Tooltip, LinearProgress, Grid
} from "@mui/material";
import {
  ChevronDown, ChevronRight, Copy, Check, Play, Code, Server, Activity, Settings, ExternalLink
} from "lucide-react";

// Import configurations and helper function from the modular data file
import { methodConfig, difficultyConfig, getParameterType } from "./apiData"; 

const ApiSection = ({ endpointData, activeEndpoint, setActiveEndpoint, copiedCode, copyToClipboard }) => {
    const ep = endpointData;
    const isActive = activeEndpoint === ep.id;
    const method = ep.method;
    const [loading, setLoading] = useState(false);

    const handleTryEndpoint = () => {
      setLoading(true);
      setTimeout(() => setLoading(false), 2000);
    };

    return (
        <Card 
            id={`endpoint-${ep.id}`} 
            key={ep.id} 
            sx={{ 
                mb: 3,
                overflow: "visible",
                border: "1px solid",
                borderColor: isActive ? methodConfig[method].color : "divider"
            }}
        >
            {/* Clickable Header/Summary */}
            <Box
                sx={{
                    p: 3,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { 
                        bgcolor: "#f8fafc",
                        borderColor: methodConfig[method].color
                    },
                }}
                onClick={() => setActiveEndpoint(isActive ? null : ep.id)}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Chip 
                            label={ep.method} 
                            sx={{ 
                                bgcolor: methodConfig[method].color,
                                color: "white",
                                fontWeight: "bold",
                                minWidth: 60
                            }}
                        />
                        <Typography 
                            variant="h6" 
                            fontFamily="monospace" 
                            sx={{ color: "#374151" }}
                        >
                            {ep.endpoint}
                        </Typography>
                        <Chip 
                            label={ep.category}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                        />
                    </Stack>
                    {isActive ? 
                        <ChevronDown color={methodConfig[method].color} /> : 
                        <ChevronRight color={methodConfig[method].color} />
                    }
                </Stack>

                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {ep.title}
                </Typography>
                <Typography color="text.secondary" paragraph>
                    {ep.description}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                    <Tooltip title="Difficulty Level">
                        <Chip 
                            label={ep.difficulty}
                            size="small"
                            sx={{ 
                                bgcolor: difficultyConfig[ep.difficulty].color,
                                color: "white",
                                fontWeight: "bold"
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Average Response Time">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Activity size={16} color="#10B981" />
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                                {ep.responseTime}
                            </Typography>
                        </Box>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={isActive}>
                <Box sx={{ bgcolor: "#fafbfc", borderTop: "1px solid #e5e7eb" }}>
                    {loading && <LinearProgress />}
                    <Box sx={{ p: 4 }}>
                        {/* Parameters Table */}
                        {ep.parameters && ep.parameters.length > 0 && (
                            <Box mb={4}>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                                    <Settings size={20} style={{ marginRight: 8 }} />
                                    Parameters
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead sx={{ bgcolor: "#f8fafc" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>Required</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>Example</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {ep.parameters.map((param, i) => (
                                                <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                                                    <TableCell>
                                                        <Typography fontFamily="monospace" fontWeight="bold" color="#374151">
                                                            {param.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={param.type} 
                                                            size="small" 
                                                            variant="outlined"
                                                            sx={{ fontFamily: "monospace" }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={getParameterType(param)} 
                                                            size="small"
                                                            color="default"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={param.required ? "Required" : "Optional"}
                                                            color={param.required ? "error" : "default"}
                                                            variant={param.required ? "filled" : "outlined"}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{param.description}</TableCell>
                                                    <TableCell>
                                                        {param.example && (
                                                            <Typography 
                                                                variant="body2" 
                                                                fontFamily="monospace"
                                                                sx={{ 
                                                                    bgcolor: "#f1f5f9", 
                                                                    p: 1, 
                                                                    borderRadius: 1,
                                                                    display: "inline-block"
                                                                }}
                                                            >
                                                                {param.example}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {/* Request Body Example */}
                        {ep.requestBody && (
                            <Box mb={4}>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                                    <Code size={20} style={{ marginRight: 8 }} />
                                    Request Body Example (JSON)
                                </Typography>
                                <Box sx={{ 
                                    bgcolor: "#1a1a1a", 
                                    color: "#f8c471", 
                                    p: 3, 
                                    borderRadius: 1, 
                                    fontFamily: "monospace", 
                                    fontSize: 13,
                                    whiteSpace: "pre-wrap",
                                    overflow: "auto"
                                }}>
                                    {ep.requestBody}
                                </Box>
                            </Box>
                        )}


                        {/* Code Examples (cURL and Response) */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={6}>
                                <Card variant="outlined">
                                    <Box sx={{ p: 2, bgcolor: "#1a1a1a", color: "white", borderRadius: "4px 4px 0 0" }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                                                <Code size={18} style={{ marginRight: 8 }} />
                                                cURL
                                            </Typography>
                                            <Tooltip title="Copy to clipboard">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => copyToClipboard(ep.curlExample, `curl-${ep.id}`)}
                                                    sx={{ color: "white" }}
                                                >
                                                    {copiedCode === `curl-${ep.id}` ? 
                                                        <Check color="success" size={18} /> : 
                                                        <Copy size={18} />
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                    <Box
                                        sx={{
                                            bgcolor: "#1a1a1a",
                                            color: "#4ade80",
                                            p: 3,
                                            fontFamily: "monospace",
                                            fontSize: 13,
                                            whiteSpace: "pre-wrap",
                                            overflow: "auto",
                                            maxHeight: 300,
                                            borderRadius: "0 0 4px 4px"
                                        }}
                                    >
                                        {ep.curlExample}
                                    </Box>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Card variant="outlined">
                                    <Box sx={{ p: 2, bgcolor: "#1a1a1a", color: "white", borderRadius: "4px 4px 0 0" }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                                                <Server size={18} style={{ marginRight: 8 }} />
                                                Response ({ep.response.status})
                                            </Typography>
                                            <Tooltip title="Copy to clipboard">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => copyToClipboard(ep.response.body, `response-${ep.id}`)}
                                                    sx={{ color: "white" }}
                                                >
                                                    {copiedCode === `response-${ep.id}` ? 
                                                        <Check color="success" size={18} /> : 
                                                        <Copy size={18} />
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                    <Box
                                        sx={{
                                            bgcolor: "#1a1a1a",
                                            color: "#64b5f6",
                                            p: 3,
                                            fontFamily: "monospace",
                                            fontSize: 13,
                                            whiteSpace: "pre-wrap",
                                            overflow: "auto",
                                            maxHeight: 300,
                                            borderRadius: "0 0 4px 4px"
                                        }}
                                    >
                                        {ep.response.body}
                                    </Box>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Try Button */}
                        <Box sx={{ mt: 4, textAlign: "center" }}>
                            <Button 
                                variant="contained" 
                                size="large"
                                startIcon={<Play />}
                                endIcon={<ExternalLink size={16} />}
                                onClick={handleTryEndpoint}
                                disabled={loading}
                                sx={{ 
                                    bgcolor: methodConfig[method].color,
                                    "&:hover": { 
                                        bgcolor: methodConfig[method].color,
                                        filter: "brightness(0.9)" 
                                    },
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: "bold"
                                }}
                            >
                                {loading ? "Testing..." : "Try this endpoint"}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Collapse>
        </Card>
    );
};

export default ApiSection;